import { ISourcingRunCounts } from '../types'

export interface IVibeCheckProgress {
  decided: number
  total: number
}

/**
 * How many creators a run actually sourced — every status, including
 * `ready_for_shortlist`.
 *
 * This is the single definition of "how big is this run", and everything
 * that renders that number goes through it (BRAND-9592). Before, the
 * "Showing X of N Creators" panel took its denominator from a progress
 * helper that EXCLUDED `ready_for_shortlist`, while its numerator counted
 * the rows on screen — and the listing keeps showing a creator after they
 * answer Interested (their row switches to the BRAND-9587 "Ready for
 * shortlist" pill). So each Interested creator widened the gap by one and
 * the count ran past its own total: "Showing 12 of 11".
 *
 * The same identity makes it agree with the run's channel-count badge, which
 * the BE derives by grouping every sourced row for the run
 * (`AgentSourcedCreatorQueries.summary_counts`) and therefore also counts
 * `ready_for_shortlist`. Every number now means "creators this run sourced".
 *
 * Enumerates the statuses explicitly rather than summing every key of
 * `counts`, so a new bucket added to ISourcingRunCounts that is NOT a
 * creator status can't silently inflate the total — but that also means a
 * genuinely new status must be added here. `AgentSourcedCreator`'s enum is
 * exactly these four (app/models/agent_sourced_creator.rb).
 */
export const computeSourcedTotal = (counts: ReadonlyArray<ISourcingRunCounts>): number =>
  counts.reduce((acc, c) => acc + c.pending + c.approved + c.rejected + c.ready_for_shortlist, 0)

/**
 * Computes "{decided} of {total} vibe checked" for one or more sourcing runs.
 *
 * Both halves count `ready_for_shortlist`, and they have to: a creator only
 * reaches that status by being APPROVED and then answering Interested on the
 * intent link. Excluding them (as this did before BRAND-9592) dropped them
 * from `decided` and `total` at the same moment, so the label ticked
 * BACKWARDS — "5 of 12 vibe checked" became "4 of 11" because someone
 * accepted an invite. Counting them in both keeps it monotonic: a decision,
 * once made, stays made.
 *
 * `total` is `computeSourcedTotal` by construction rather than a second
 * hand-rolled sum, so this label, the "Showing X of N Creators" panel and
 * the BE-derived channel-count badge cannot disagree about how many
 * creators a run holds.
 *
 * Accepts an array so the same function serves both a single selected run
 * and the aggregate All-runs view (per-run counts are disjoint, so summing
 * is safe). A run that completed with 0 creators imported (a "born
 * completed" rerun) naturally yields `{ decided: 0, total: 0 }`.
 */
export const computeVibeCheckProgress = (
  counts: ReadonlyArray<ISourcingRunCounts>,
): IVibeCheckProgress => ({
  decided: counts.reduce((acc, c) => acc + c.approved + c.rejected + c.ready_for_shortlist, 0),
  total: computeSourcedTotal(counts),
})
